import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Eye, 
  Edit, 
  Trash2,
  FileText,
  Filter
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { formatCurrency, formatDate } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../api/clients';

export function InstallmentList() {
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('');
  const queryClient = useQueryClient();

  const params: any = {};
  if (status) params.estado = status;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['installments', { status }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (status) params.append('estado', status);
      return api.get(`/installments?${params}`).then(res => res.data);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/installments/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['installments'] });
      toast.success('Contrato eliminado exitosamente');
      refetch();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al eliminar el contrato');
    },
  });

  const handleDelete = (id: number, numero: string) => {
    if (!confirm(`¿Estás seguro de eliminar el contrato ${numero}?`)) return;
    deleteMutation.mutate(id);
  };

  const contratos = data?.data || [];
  const contratosFiltrados = contratos.filter((contrato: any) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      contrato.clienteNombre?.toLowerCase().includes(searchLower) ||
      contrato.clienteDni?.toLowerCase().includes(searchLower) ||
      contrato.numeroContrato?.toLowerCase().includes(searchLower) ||
      contrato.productoNombre?.toLowerCase().includes(searchLower)
    );
  });

  const getStatusBadge = (estado: string) => {
    const variants: Record<string, { variant: 'success' | 'danger' | 'warning' | 'info' | 'default'; label: string }> = {
      activo: { variant: 'info', label: 'Activo' },
      moroso: { variant: 'danger', label: 'Moroso' },
      completado: { variant: 'success', label: 'Completado' },
      cancelado: { variant: 'default', label: 'Cancelado' },
    };
    const config = variants[estado] || variants.activo;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Cargando contratos...</div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-8 text-red-500">
        Error al cargar los contratos
      </div>
    );
  }

  const total = contratosFiltrados.length;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Contratos en Cuotas</h1>
          <p className="text-sm text-gray-500">
            {total} contrato(s) registrados
          </p>
        </div>
        <Link to="/cuotas/nuevo">
          <Button>
            <Plus size={18} />
            Nuevo Contrato
          </Button>
        </Link>
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Buscar por nombre, DNI o producto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          )}
        </div>
        
        <div className="flex items-center gap-2 min-w-45">
          <Filter size={16} className="text-gray-400 shrink-0" />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all duration-200"
          >
            <option value="">Todos los estados</option>
            <option value="activo">Activo</option>
            <option value="moroso">Moroso</option>
            <option value="completado">Completado</option>
            <option value="cancelado">Cancelado</option>
          </select>
        </div>
      </div>

      {/* Lista de contratos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {contratosFiltrados.map((contrato: any) => (
          <div key={contrato.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow">
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-800 truncate max-w-35">
                    {contrato.clienteNombre}
                  </h3>
                  {getStatusBadge(contrato.estado)}
                </div>
                <p className="text-xs text-gray-400">DNI: {contrato.clienteDni}</p>
              </div>
              <div className="flex gap-1">
                <Link
                  to={`/cuotas/${contrato.id}`}
                  className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                >
                  <Eye size={16} />
                </Link>
                {contrato.estado === 'activo' && (
                  <Link
                    to={`/cuotas/editar/${contrato.id}`}
                    className="p-1.5 text-gray-500 hover:bg-gray-50 rounded transition-colors"
                  >
                    <Edit size={16} />
                  </Link>
                )}
                {(contrato.estado === 'activo' || contrato.estado === 'cancelado' || contrato.estado === 'completado') && (
                  <button
                    onClick={() => handleDelete(contrato.id, contrato.numeroContrato)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>

            {/* Detalles */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Producto</span>
                <span className="font-medium text-gray-700 truncate max-w-37.5">
                  {contrato.productoNombre}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total</span>
                <span className="font-medium text-gray-700">
                  {formatCurrency(contrato.productoPrecioTotal)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Cuotas</span>
                <span className="font-medium text-gray-700">
                  {contrato.cuotasPagadas || 0} / {contrato.cantidadCuotas}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Saldo</span>
                <span className="font-medium text-gray-700">
                  {formatCurrency(contrato.saldoFinanciar || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Inicio</span>
                <span className="text-xs text-gray-400">
                  {formatDate(contrato.fechaInicio)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Monto cuota</span>
                <span className="text-xs text-gray-400">
                  {formatCurrency(contrato.montoCuota)}
                </span>
              </div>
            </div>

            {/* Acciones */}
            <div className="mt-3 pt-3 border-t border-gray-100 flex gap-2">
              <Link to={`/cuotas/editar/${contrato.id}`} className="flex-1">
                <button className="w-full flex items-center justify-center gap-1 text-sm text-gray-700 border border-gray-300 hover:bg-gray-50 py-1.5 rounded-lg transition-colors">
                  <Edit size={14} />
                  Editar
                </button>
              </Link>
            </div>
          </div>
        ))}
      </div>

      {contratosFiltrados.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <FileText size={48} className="mx-auto mb-4 text-gray-300" />
          <p>No se encontraron contratos</p>
          {searchTerm && (
            <p className="text-sm mt-1">
              No hay contratos que coincidan con "{searchTerm}"
            </p>
          )}
          <Link to="/cuotas/nuevo" className="text-blue-600 hover:underline text-sm mt-2 inline-block">
            Crear el primer contrato
          </Link>
        </div>
      )}
    </div>
  );
}